import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Star, Award, Sparkles, Zap
} from 'lucide-react';
import { EnhancedAnalysis, NewsArticle, EngagementMetrics, UserJourney } from '../../types/enhancedAnalysis';

interface ActionZoneProps {
  analysis: EnhancedAnalysis;
  newsArticle?: NewsArticle;
  userEngagement: EngagementMetrics;
  user?: any;
  onRegister: () => void;
  onContinueLearning: () => void;
  onExploreMore: () => void;
  readingProgress: number;
}

const ActionZone: React.FC<ActionZoneProps> = ({
  analysis,
  newsArticle,
  userEngagement,
  user,
  onRegister,
  onContinueLearning,
  onExploreMore,
  readingProgress
}) => {
  const [showCelebration, setShowCelebration] = useState(false);
  const [userJourney, setUserJourney] = useState<UserJourney>({
    stage: 'reading',
    readingProgress,
    engagementScore: 0,
    learningIntent: 'low',
    nextBestAction: {
      id: 'continue_learning',
      title: '继续学习',
      description: '探索更多分析方法',
      type: 'read',
      priority: 'medium',
      estimatedTime: 15,
      requiresAuth: false
    },
    timeSpent: userEngagement.timeSpent,
    sectionsCompleted: userEngagement.sectionsViewed
  });

  // 计算用户参与度评分
  useEffect(() => {
    const engagementScore = calculateEngagementScore();
    const learningIntent = determineLearningIntent(engagementScore);
    const stage = determineUserStage();
    
    setUserJourney(prev => ({
      ...prev,
      engagementScore,
      learningIntent,
      stage,
      readingProgress
    }));

    // 如果用户完成阅读，显示庆祝动画
    if (readingProgress >= 90 && !showCelebration) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
  }, [readingProgress, userEngagement]);

  const calculateEngagementScore = (): number => {
    let score = 0;
    
    // 阅读进度权重 (40%)
    score += (readingProgress / 100) * 0.4;
    
    // 停留时间权重 (30%)
    const timeScore = Math.min(userEngagement.timeSpent / 600, 1); // 10分钟为满分
    score += timeScore * 0.3;
    
    // 互动行为权重 (30%)
    let interactionScore = 0;
    if (userEngagement.methodologyViewed) interactionScore += 0.3;
    // 移除分享尝试评分
    if (userEngagement.bookmarked) interactionScore += 0.2;
    if (userEngagement.sectionsViewed.length > 2) interactionScore += 0.3;
    
    score += Math.min(interactionScore, 1) * 0.3;
    
    return Math.min(score, 1);
  };

  const determineLearningIntent = (engagementScore: number): 'low' | 'medium' | 'high' => {
    if (engagementScore >= 0.7) return 'high';
    if (engagementScore >= 0.4) return 'medium';
    return 'low';
  };

  const determineUserStage = () => {
    if (readingProgress >= 90) return 'completed';
    if (userEngagement.methodologyViewed) return 'learning';
    if (userEngagement.timeSpent > 180) return 'engaged';
    return 'reading';
  };

  // 移除getNextBestActions函数

  const getCompletionMessage = () => {
    if (userJourney.engagementScore >= 0.8) {
      return {
        title: '🎉 太棒了！您已深度掌握这篇分析',
        subtitle: '您的学习能力令人印象深刻，继续保持这种深度思考的习惯！',
        level: 'excellent'
      };
    } else if (userJourney.engagementScore >= 0.6) {
      return {
        title: '👏 很好！您认真阅读了这篇分析',
        subtitle: '继续探索更多内容，提升您的分析思维能力。',
        level: 'good'
      };
    } else {
      return {
        title: '👏 已完成',
        subtitle: '希望这篇分析对您有所启发，欢迎继续探索。',
        level: 'basic'
      };
    }
  };

  const completionMessage = getCompletionMessage();
  // 移除nextActions相关代码

  return (
    <section className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 rounded-xl border border-green-200 p-8 mb-8 relative overflow-hidden">
      {/* 庆祝动画背景 */}
      {showCelebration && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 left-4 animate-bounce">
            <Sparkles className="w-6 h-6 text-yellow-500" />
          </div>
          <div className="absolute top-8 right-8 animate-bounce delay-100">
            <Star className="w-5 h-5 text-blue-500" />
          </div>
          <div className="absolute bottom-8 left-8 animate-bounce delay-200">
            <Award className="w-5 h-5 text-purple-500" />
          </div>
        </div>
      )}

      {/* 移除阅读成就统计区域 */}



      {/* 移除推荐下一步行动区域 */}

      {/* 移除加入学习社区区域 */}
    </section>
  );
};

export default ActionZone;